// ⚙ Pricing rules — global margin floors (hard/soft) editable by admins.
// Opened from the Line Items header. Read-only for non-admins.
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { X, Settings2, Save } from "lucide-react";
import api from "../../../lib/api";
import { toast } from "sonner";

export default function PricingRulesModal({ open, isAdmin, onClose, onSaved }) {
  const [rules, setRules] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get("/config/pricing-rules").then(({ data }) => setRules(data)).catch(() => {});
  }, [open]);

  if (!open) return null;

  const set = (k, v) => setRules((r) => ({ ...r, [k]: v }));
  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/config/pricing-rules", {
        hard_floor_pct: Number(rules.hard_floor_pct) || 0,
        soft_floor_pct: Number(rules.soft_floor_pct) || 0,
        ceiling_pct: Number(rules.ceiling_pct) || 30,
        hard_floor_blocks: !!rules.hard_floor_blocks,
      });
      setRules(data);
      onSaved?.(data);
      toast.success("Pricing rules saved");
      onClose();
    } catch (e) {
      toast.error("Save failed", { description: e?.response?.data?.detail || e.message });
    } finally { setSaving(false); }
  };

  return createPortal(
    <div className="fixed inset-0 z-[135] flex items-center justify-center p-4"
         style={{ background: "rgba(0,0,0,0.65)", backdropFilter: "blur(4px)" }}
         onClick={onClose} data-testid="pricing-rules-overlay">
      <div onClick={(e) => e.stopPropagation()} data-testid="pricing-rules-modal"
           style={{ width: 420, maxWidth: "100%", background: "var(--t2-surface2, #0E1524)", border: "1px solid var(--t2-border2)", borderRadius: 14, overflow: "hidden" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 18px", borderBottom: "1px solid var(--t2-border)" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <Settings2 className="w-4 h-4" style={{ color: "var(--t2-orange-ink)" }} />
            <span style={{ fontSize: 14, fontWeight: 700, color: "var(--t2-white)" }}>Pricing rules</span>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", color: "var(--t2-grey3)", cursor: "pointer" }} data-testid="pricing-rules-close"><X className="w-4 h-4" /></button>
        </div>
        {!rules ? (
          <div style={{ padding: 40, textAlign: "center", color: "var(--t2-grey3)" }}>Loading…</div>
        ) : (
          <div style={{ padding: 18 }}>
            <p className="t2-mono" style={{ fontSize: 10.5, color: "var(--t2-grey3)", marginBottom: 16, lineHeight: 1.5 }}>
              Global margin floors used across the Quote Builder. Margins below the soft floor warn;
              below the hard floor require confirmation{rules.hard_floor_blocks ? " — currently BLOCKED" : ""}.
            </p>
            {[["hard_floor_pct", "Hard floor %", "Minimum acceptable margin"],
              ["soft_floor_pct", "Soft floor %", "Healthy-margin target"],
              ["ceiling_pct", "Ceiling %", "Slider max for lines with no retail on file"]].map(([k, lbl, hint]) => (
              <div key={k} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: "var(--t2-white)" }}>{lbl}</div>
                  <div className="t2-mono" style={{ fontSize: 10, color: "var(--t2-grey4)" }}>{hint}</div>
                </div>
                <div style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                  <input type="number" step="0.1" min="0" max="100" disabled={!isAdmin}
                    value={rules[k] ?? 0} onChange={(e) => set(k, e.target.value)}
                    data-testid={`pricing-rules-${k}`}
                    className="t2-input t2-mono" style={{ width: 74, padding: "6px 10px", textAlign: "right", fontSize: 13, fontWeight: 700 }} />
                  <span className="t2-mono" style={{ color: "var(--t2-grey3)", fontSize: 11 }}>%</span>
                </div>
              </div>
            ))}
            <label style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 8, cursor: isAdmin ? "pointer" : "default" }}>
              <input type="checkbox" disabled={!isAdmin} checked={!!rules.hard_floor_blocks}
                onChange={(e) => set("hard_floor_blocks", e.target.checked)} data-testid="pricing-rules-blocks" />
              <span style={{ fontSize: 12.5, color: "var(--t2-grey1)" }}>Hard floor blocks saving (else warn + confirm)</span>
            </label>
            {isAdmin ? (
              <button onClick={save} disabled={saving} data-testid="pricing-rules-save"
                style={{ width: "100%", marginTop: 18, padding: "9px 0", borderRadius: 8, border: "none", cursor: "pointer",
                  background: "var(--t2-orange)", color: "#000", fontSize: 13, fontWeight: 700, display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
                <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save rules"}
              </button>
            ) : (
              <p className="t2-mono" style={{ fontSize: 10, color: "var(--t2-grey4)", marginTop: 16, textAlign: "center" }}>Admin access required to edit.</p>
            )}
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}
